import { Gender, IVector3 } from '../../types';
import {
	ClothesComponentId,
	ClothesType,
	fromCustomToGameComponentId,
	fromGameToCustomComponentId,
} from '../../types/clothes';
import { ConfigParser } from '../ConfigParser';

export const PROP_CLOTHES_TYPES: ClothesType[] = [
	ClothesType.HAT,
	ClothesType.GLASSES,
	ClothesType.EARRINGS,
	ClothesType.WATCHES,
	ClothesType.BRACELET,
];

export class ClothesConfig {
	public readonly items: ClothesItemConfig[] = [];
	public readonly outfits: ClothesOutfitConfig[] = [];
	public readonly shops: ClothesShopConfig[] = [];

	private readonly clothesById = new Map<string, ClothesItemConfig>();
	private readonly outfitsById = new Map<string, ClothesOutfitConfig>();

	constructor(tables: Record<string, any>) {
		const clothesShops = tables['shops'];
		for (const shop of clothesShops) {
			this.shops.push(new ClothesShopConfig(shop));
		}

		const clothesMaxStockIndexByComponentId = new Map<number, number>();
		const propsMaxStockIndexByComponentId = new Map<number, number>();

		for (const rawIndex of tables['stock_max_indices']) {
			const config = new ClothesStockMaxIndexConfig(rawIndex);
			if (config.isProp) {
				propsMaxStockIndexByComponentId.set(config.componentId, config.maxIndex);
			} else {
				clothesMaxStockIndexByComponentId.set(config.componentId, config.maxIndex);
			}
		}

		const clothesItems = [...tables['items_male']];
		const clothesItemUniqueIDs: string[] = [];

		for (const item of clothesItems) {
			const clothesItemId = ConfigParser.getString(item, 'id');

			const clothesItem = new ClothesItemConfig(clothesItemId, item);

			const absoluteDrawableId = this.getAbsoluteDrawableId(
				clothesItem,
				clothesMaxStockIndexByComponentId,
				propsMaxStockIndexByComponentId,
			);
			clothesItem.drawableId = absoluteDrawableId;

			if (!clothesItemUniqueIDs.includes(clothesItemId)) {
				clothesItemUniqueIDs.push(clothesItemId);
			} else {
				throw new Error(`Clothes ${clothesItem.gender} item with ID "${clothesItemId}" is duplicated`);
			}

			if (this.clothesById.has(clothesItemId)) {
				throw new Error(`Clothes item with ID "${clothesItemId}" already exists`);
			} else {
				this.clothesById.set(clothesItemId, clothesItem);
				this.items.push(clothesItem);
			}
		}

		const clothesOutfits = [...tables['outfits_male']];
		const clothesOutfitsUniqueIDs: string[] = [];

		for (const item of clothesOutfits) {
			const clothesOutfitId = ConfigParser.getString(item, 'id');

			const clothesOutfit = new ClothesOutfitConfig(this, clothesOutfitId, item);

			if (!clothesOutfitsUniqueIDs.includes(clothesOutfitId)) {
				clothesOutfitsUniqueIDs.push(clothesOutfitId);
			} else {
				throw new Error(`Clothes ${clothesOutfit.gender} putfit with ID "${clothesOutfitId}" is duplicated`);
			}

			if (this.outfitsById.has(clothesOutfitId)) {
				throw new Error(`Clothes outfit with ID "${clothesOutfitId}" already exists`);
			} else {
				this.outfitsById.set(clothesOutfitId, clothesOutfit);
				this.outfits.push(clothesOutfit);
			}
		}
	}

	private getAbsoluteDrawableId(
		item: ClothesItemConfig,
		clothesMaxStockIndexByComponentId: Map<number, number>,
		propsMaxStockIndexByComponentId: Map<number, number>,
	): number {
		if (item.drawableId !== -1) {
			return item.drawableId;
		}

		const componentId = fromCustomToGameComponentId(item.componentId);
		if (
			(!item.hideGlasses && !clothesMaxStockIndexByComponentId.has(componentId)) ||
			(item.isProp && !propsMaxStockIndexByComponentId.has(componentId)) ||
			item.relativeDrawableId === null
		) {
			return -1;
		}

		const maxStockIndex = (item.isProp ? propsMaxStockIndexByComponentId : clothesMaxStockIndexByComponentId).get(
			componentId,
		);
		if (maxStockIndex === undefined) {
			return -1;
		}

		return maxStockIndex + 1 + item.relativeDrawableId;
	}

	public getOutfitById(id: string): ClothesOutfitConfig | null {
		return this.outfitsById.get(id) ?? null;
	}

	public getClothesItemById(id: string): ClothesItemConfig | null {
		return this.clothesById.get(id) ?? null;
	}

	public getClientConfig() {
		return {
			items: this.items.map((e) => e.getClientConfig()),
			outfits: this.outfits,
			shops: this.shops,
		};
	}
}

export class ClothesShopConfig {
	public readonly id: string;
	public readonly name: string;
	public readonly group: string;
	public readonly blipSprite: number;
	public readonly blipColor: number;
	public readonly markerPosition: IVector3;
	public readonly previewPosition: IVector3;
	public readonly previewHeading: number;
	public readonly pedPosition: IVector3;
	public readonly pedHeading: number;
	public readonly pedModel: string;

	constructor(config: Record<string, any>) {
		this.id = ConfigParser.getString(config, 'id');
		this.name = ConfigParser.getString(config, 'name');
		this.group = ConfigParser.getString(config, 'group');
		this.blipSprite = ConfigParser.getInt(config, 'blip_sprite');
		this.blipColor = ConfigParser.getInt(config, 'blip_color');
		this.markerPosition = ConfigParser.getVector3(config, 'marker_position');
		this.previewPosition = ConfigParser.getVector3(config, 'preview_position');
		this.previewHeading = ConfigParser.getFloat(config, 'preview_heading');
		this.pedPosition = ConfigParser.getVector3(config, 'ped_position');
		this.pedHeading = ConfigParser.getFloat(config, 'ped_heading');
		this.pedModel = ConfigParser.getString(config, 'ped_model');
	}
}

export class ClothesItemConfig {
	public readonly id: string;
	public readonly gender: Gender;
	public readonly type: ClothesType | null;
	public readonly isProp: boolean;

	public readonly name: string;
	public readonly shopGroup: string | null;
	public readonly price: number | null;

	public readonly torsoDrawableId: number | null;
	public readonly torsoTextureId: number | null;
	public readonly undershirtDrawableId: number | null;
	public readonly undershirtTextureId: number | null;

	public readonly componentId: ClothesComponentId;
	public drawableId: number;
	public readonly relativeDrawableId: number | null;
	public readonly textureId: number;

	public readonly isEnabled: boolean;

	public readonly hideHat: boolean;
	public readonly hideGlasses: boolean;
	public readonly needOpenTorso: boolean;

	constructor(id: string, config: Record<string, any>) {
		this.id = id;

		this.gender = ConfigParser.getEnum(config, 'gender', Gender);
		this.type = ConfigParser.getEnum(config, 'type', ClothesType);
		this.isProp = PROP_CLOTHES_TYPES.includes(this.type);

		this.name = ConfigParser.getString(config, 'name');
		this.shopGroup = ConfigParser.getString(config, 'shop_group', null);
		this.price = ConfigParser.getInt(config, 'price', null);

		this.torsoDrawableId = ConfigParser.getInt(config, 'torso_drawable_id', null);
		this.torsoTextureId = ConfigParser.getInt(config, 'torso_texture_id', null);
		this.undershirtDrawableId = ConfigParser.getInt(config, 'undershirt_drawable_id', null);
		this.undershirtTextureId = ConfigParser.getInt(config, 'undershirt_texture_id', null);

		const componentId = ConfigParser.getInt(config, 'component_id');
		this.componentId = fromGameToCustomComponentId(componentId, this.isProp);

		this.drawableId = ConfigParser.getInt(config, 'drawable_id', -1);
		this.relativeDrawableId = ConfigParser.getInt(config, 'rel_drawable_id', null);
		if (this.drawableId === -1 && this.relativeDrawableId === null) {
			throw new Error(`No drawable id was specified for item "${this.id}"`);
		}

		this.textureId = ConfigParser.getInt(config, 'texture_id');

		this.isEnabled = ConfigParser.getBool(config, 'is_enabled');

		this.hideHat = ConfigParser.getBool(config, 'hide_hat');
		this.hideGlasses = ConfigParser.getBool(config, 'hide_glasses');
		this.needOpenTorso = ConfigParser.getBool(config, 'need_open_torso');
	}

	public getClientConfig() {
		return {
			id: this.id,

			gender: this.gender,
			type: this.type,
			isProp: this.isProp,

			name: this.name,
			shopGroup: this.shopGroup,
			price: this.price,

			torsoDrawableId: this.torsoDrawableId,
			torsoTextureId: this.torsoTextureId,
			undershirtDrawableId: this.undershirtDrawableId,
			undershirtTextureId: this.undershirtTextureId,

			componentId: this.componentId,
			drawableId: this.drawableId,
			textureId: this.textureId,

			isEnabled: this.isEnabled,

			hideHat: this.hideHat,
			hideGlasses: this.hideGlasses,
			needOpenTorso: this.needOpenTorso,
		};
	}
}

export class ClothesOutfitConfig {
	public readonly id: string;
	public readonly gender: Gender;
	public readonly items: string[];

	public readonly name: string;
	public readonly shopGroup: string | null;
	public readonly price: number | null;

	public readonly isEnabled: boolean;

	constructor(clothes: ClothesConfig, id: string, config: Record<string, any>) {
		this.id = id;

		this.gender = ConfigParser.getEnum(config, 'gender', Gender);
		this.items = ConfigParser.getStringArray(config, 'items');
		for (const itemId of this.items) {
			if (!clothes.getClothesItemById(itemId)) {
				throw new Error(`Clothes item with ID "${itemId}" not found (in outfit with ID ${this.id})`);
			}
		}

		this.name = ConfigParser.getString(config, 'name');
		this.shopGroup = ConfigParser.getString(config, 'shop_group', null);
		this.price = ConfigParser.getInt(config, 'price', null);

		this.isEnabled = ConfigParser.getBool(config, 'is_enabled');
	}
}

export class ClothesStockMaxIndexConfig {
	public readonly componentId: number;
	public readonly maxIndex: number;
	public readonly isProp: boolean;

	constructor(config: Record<string, any>) {
		this.componentId = ConfigParser.getInt(config, 'component_id');
		this.maxIndex = ConfigParser.getInt(config, 'max_index');
		this.isProp = ConfigParser.getBool(config, 'is_prop');
	}
}
